import { useEffect } from 'react';
import { Route, Router, Switch } from 'react-router-dom';
import history from 'utils/history';
import { Helmet } from 'react-helmet';

import { UserContext } from 'contexts/UserContext';
import { useBrand, BRANDS } from 'hooks/useBrand';
import AV from 'images/favicon.ico';
import ECOSU from 'images/favicon-ecosu.ico';
import { metrika } from 'utils/metrika';
import { withTranslations } from 'utils/i18n';
import { ServiceNotificationModal } from 'components/ServiceNotificationModal';
import { VendExpertWarningModal } from 'components/VendExpertWarningModal';
import { ConfigProvider, theme } from 'antd';
import { FeatureFlagModal } from 'features/flags';

import Report, { REPORT_URL, TestReport } from 'containers/Report';
import { ScrollToTop } from 'components/ScrollToTop';
import { useBrandFeatures } from 'hooks/useBrandFeatures';
import { useLoadConfig } from 'hooks/useConfig';
import { ServiceNotification } from 'features/serviceNotification';
import { MaintenancePage } from 'containers/SystemMaintenance';
import { useIsDark } from 'ui-kit';
import Main from './containers/Main/Main';
import './App.scss';

const FAVICONS: Record<BRANDS, string> = {
  [BRANDS.AV]: AV,
  [BRANDS.ECOSU]: ECOSU,
  [BRANDS.WODOMAT]: AV
};

function Root() {
  const [isDark] = useIsDark();
  const isLoading = useLoadConfig();

  const brand = useBrand();

  useEffect(
    () =>
      history.listen((l) => {
        metrika.hit(l.pathname);
      }),
    []
  );

  const features = useBrandFeatures();

  return (
    <ConfigProvider
      theme={{
        algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
        token: {
          colorPrimary: '#4f637e'
        }
      }}
    >
      <ServiceNotification />
      <Router history={history}>
        <Switch>
          <Route path="/maintenance" exact component={MaintenancePage} />
          <Route path={REPORT_URL} exact>
            <Report />
          </Route>
          <Route path={`${REPORT_URL}/test`} exact>
            <TestReport />
          </Route>
          <Route>
            <UserContext>
              {features.services && <ServiceNotificationModal />}
              <VendExpertWarningModal />
              {!isLoading && <Main />}
            </UserContext>
          </Route>
        </Switch>
        <Helmet>
          <link rel="shortcut icon" href={FAVICONS[brand]} />
        </Helmet>
        <ScrollToTop />
      </Router>
      <FeatureFlagModal />
    </ConfigProvider>
  );
}

export const App = withTranslations(Root);
